import React from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  UserIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { getDealRoomBrandedName } from '../../../utils/locale';

// Profile Incomplete Empty State - styled to match ValuationEmptyState pattern
const ProfileIncompleteEmptyState = ({ profilePercentage = 0, company, className = '' }) => {
  const intl = useIntl();
  const dealRoomName = getDealRoomBrandedName(company);
  const features = [
    {
      icon: MagnifyingGlassIcon,
      title: intl.formatMessage({ id: 'succession.search.list_search.find_partners_title' }),
      description: intl.formatMessage({ id: 'succession.search.list_search.find_partners_description' }),
    },
    {
      icon: UserGroupIcon,
      title: intl.formatMessage({ id: 'succession.search.list_search.discover_matches_title' }),
      description: intl.formatMessage({ id: 'succession.search.list_search.discover_matches_description' }),
    },
    {
      icon: ShieldCheckIcon,
      title: intl.formatMessage({ id: 'succession.search.list_search.connect_securely_title' }),
      description: intl.formatMessage({ id: 'succession.search.list_search.connect_securely_description' }),
    },
  ];

  return (
    <div className={`flex flex-col items-center py-6 px-4 ${className}`}>
      {/* Main bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-succession-bg-secondary rounded-2xl border border-succession-border p-8 md:p-10 max-w-2xl text-center"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-succession-primary/10 mb-6"
        >
          <MagnifyingGlassIcon className="w-10 h-10 text-succession-primary" />
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold mb-4 text-succession-text-primary"
        >
          {intl.formatMessage({ id: 'succession.search.list_search.complete_profile_title' })}
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-succession-text-secondary text-lg mb-6 leading-relaxed"
        >
          {intl.formatMessage({ id: 'succession.search.list_search.complete_profile_description' })}
        </motion.p>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left"
        >
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-succession-bg-primary">
              <feature.icon className="w-5 h-5 text-succession-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-succession-text-primary text-sm">{feature.title}</p>
                <p className="text-xs text-succession-text-secondary">{feature.description}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <a
            href="/profile/getting_started"
            className="inline-flex items-center gap-2 bg-succession-primary hover:bg-succession-primary/90 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            <UserIcon className="w-5 h-5" />
            {intl.formatMessage({ id: 'succession.search.list_search.complete_profile_button' })}
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>

          <p className="text-sm text-succession-text-secondary mt-4">
            {intl.formatMessage({ id: 'succession.search.list_search.profile_progress' }, { percentage: profilePercentage })}
          </p>
        </motion.div>
      </motion.div>

      {/* Info Banner - Outside the bordered card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4 mt-6 max-w-2xl w-full"
      >
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-succession-text-primary mb-1">
              {intl.formatMessage({ id: 'succession.search.list_search.why_complete_profile_title' })}
            </p>
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.search.list_search.why_complete_profile_description' }, { dealRoomName })}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfileIncompleteEmptyState;
